import { ChangeDetectionStrategy, Component, ElementRef, inject, input, viewChildren } from '@angular/core'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'
import { AviationLiabilitiesForm } from '../../../models/coverages/base-coverage.model'
import { AVIATION_LIABILITIES_PANELS, GENERAL_PANEL } from '../../../models/coverages/coverage-panels.model'
import { AircraftLimitListComponent } from '@features/insured/components/coverages/aircraft-limit-list/aircraft-limit-list.component'
import { SubCoveragePanelsComponent } from '../sub-coverage-panels/sub-coverage-panels.component'
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion'
import { InsuredBaseForm } from '../../insured-base-form.directive'
import { AlbertPeriodComponent } from '@shared/components/albert-period/albert-period.component'
import { AlbertOverrideWrapper } from '@shared/components/albert-override-wrapper/albert-override-wrapper'
import { DetailviewNavigationService } from '@services/detailview-navigation.service'

/**
 * Aviation Liabilities coverage form: the General panel (period) and one panel per liability
 * sub-coverage, with the aircraft limit list under Third Party War Excess Liability.
 *
 * @example
 * <albert-aviation-liabilities-form [form]="aviationLiabilitiesForm" [isCreateMode]="false" [mainIndex]="3" />
 */
@Component({
  selector: 'albert-aviation-liabilities-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SubCoveragePanelsComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    AlbertPeriodComponent,
    AlbertOverrideWrapper,
    AircraftLimitListComponent
  ],
  templateUrl: './aviation-liabilities-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AviationLiabilitiesFormComponent extends InsuredBaseForm<AviationLiabilitiesForm> {
  /** Coverage form group, owned by the insured detailview */
  public override readonly form = input.required<FormGroup<AviationLiabilitiesForm>>()
  /** Position of this coverage in the detailview's sections, used to match nav targets */
  public readonly mainIndex = input<number>()

  /** Section navigation of the detailview, used for the panels' nav indexes */
  public readonly nav = inject(DetailviewNavigationService)

  /** Accordion value of the General panel */
  protected readonly generalPanel = GENERAL_PANEL
  /** Liability sub-coverage panels */
  protected readonly panels = AVIATION_LIABILITIES_PANELS

  private readonly generalPanelTarget = viewChildren<ElementRef<HTMLElement>>('subPanel')

  constructor() {
    super()
    this.setupPanelNavigationEffects(
      this.nav,
      this.mainIndex,
      [this.generalPanel, ...this.panels.map(panel => panel.key)],
      this.generalPanelTarget
    )
  }

  protected override getFormGroup(): FormGroup<AviationLiabilitiesForm> {
    return this.form()
  }
}
